import type {CSSProperties, ReactElement} from 'react';
import React from 'react';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE, GridItem} from '@Components/base-grid/components/grid-item';
import {GridClipartItem} from '@Components/base-grid/components/grid-clipart-item';
import {GridShapeItem} from '@Components/base-grid/components/grid-shape-item';
import {GridAnimatedStickerItem} from '@Components/base-grid/components/grid-animated-sticker-item';
import {GridUserImageItem} from '@Components/base-grid/components/grid-user-image-item';
import {repoImageThumbURL} from '@Libraries/graphic-item-image-library';
import {GridUserVideoItem} from '@Components/base-grid/components/grid-user-video-item';
import {repoVideoThumbImageURL, repoVideoThumbURL} from '@Libraries/user-video-library';
import {GridUserAudioItem} from '@Components/base-grid/components/grid-user-audio-item';
import {getAudioUrl, repoAudioThumbImageURL} from '@Libraries/user-audio-library';
import {GridImageItem} from '@Components/base-grid/components/grid-image-item';
import {GridMultipageItem} from '@Components/base-grid/components/grid-multipage-item';
import {GridButtonItem} from '@Components/base-grid/components/grid-button-item';
import {GridStockVideoItem} from '@Components/base-grid/components/grid-stock-video-item';
import {GridStockImageItem} from '@Components/base-grid/components/grid-stock-image-item';
import {GridCollectionItem} from '@Components/base-grid/components/grid-collection-item';
import {GridStockAudioItem} from '@Components/base-grid/components/grid-stock-audio-item';
import {GridEmailCampaignAudienceItem} from '@Components/base-grid/components/grid-email-campaign-audience-item';
import {GridEmailSignupFormItem} from '@Components/base-grid/components/grid-email-signup-form-item';
import {GridSkeletonItem} from '@Components/base-grid/components/grid-skeleton-item';
import {GridFancytextItem} from '@Components/base-grid/components/grid-fancytext-item/grid-fancytext-item';
import {
  getPMWStockAnimatedStickerThumbURL,
  getPMWStockEmojiThumbURL,
  getPMWStockIconThumbURL,
  getPMWStockExtractedGettyStickerThumbURL,
  getPMWStockImageThumbURL,
  getPMWStockShapeThumbURL,
  getPMWStockStillStickerThumbURL,
  shouldHideClipartItem,
  getPMWStockAiStickerThumbURL,
  getPMWStockAiIconThumbURL,
  getPMWStockAiAnimatedStickerThumbURL,
} from '@Libraries/pmw-stock-media-library';
import {PMWStockItemType} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {GridPMWStockID} from '@Components/base-grid/components/grid-pmwstockmedia-item/grid-pmw-stock-media-item';
import {GridPMWStockMediaItem} from '@Components/base-grid/components/grid-pmwstockmedia-item/grid-pmw-stock-media-item';
import {GridIconItem} from '@Components/base-grid/components/grid-icon-item';
import type {GridMediaItemTag} from '@Components/base-grid/components/grid-media-item';
import {GridStillStickerItem} from '@Components/base-grid/components/grid-still-sticker-item';
import {GridGenericButtonItem} from '@Components/base-grid/components/grid-generic-button-item/grid-generic-button-item';
import {GridExtractedGettyStickerItem} from '@Components/base-grid/components/grid-extracted-getty-sticker-item';
import {GridVideoItem} from '@Components/base-grid/components/grid-video-item/grid-video-item';

export interface BaseGridProps {
  /**
   * The id associated with the grid which keeps track of all the items.
   */
  gridId: string;
  /**
   * The id of selection group this grid is part of.
   *
   * This is used when you want to keep track of selected items accross multiple grids. For that case you would specify the same
   * gridSelectionGroupId for all these grids.
   *
   * If not specified, defaults to the group id hence creating a unique selection group for the grid.
   */
  gridSelectionGroupId?: string;
  gridItemAspectRatio?: number;
  gridItemMaxWidth?: number;
  className?: string;
  scrollingContainer?: HTMLDivElement | null;
  gridTitleClasses?: string;
  gridStyle?: CSSProperties;
  gridContainerStyle?: CSSProperties;
  gridTitle?: string;
  onSelectedItemsChanged?: Function;
  /**
   * 0 value for it means no limit on amount of selected items
   */
  maxSelectionNumber?: number;
  excludeItemWithExtension?: string;
  makeContentScrollable?: boolean;

  attachScrollListener?(cb: () => void): void;

  /**
   * If specified the default handler that selects/unselects an item doesn't fire. Instead this is called
   */
  customOnGridItemClicked?(item: GridItemStorage, isDoubleClick: boolean): void;

  onScrolledToBottom?(): void;

  gridRef?(ref: HTMLDivElement | null): void;

  gridClassName?: string;
  doSetGridInlineStyles?: boolean;
}

interface GetGridItemForStoredDataParams {
  paddedThumb?: boolean;
  item: GridItemStorage;
  gridId: string;
  gridSelectionGroupId: string;
  style: CSSProperties;
  showSelectionNumber: boolean;
  showSelectionOverlay?: boolean;
  showHoverOverlay?: boolean;
  hasTransparency?: boolean;
  className: string;
  containerClassName?: string;
  isSmallItem?: boolean;

  onClicked(e: React.MouseEvent, isDoubleClick: boolean): void;
}

export function getGridItemForStoredData(params: GetGridItemForStoredDataParams): ReactElement<any> | null {
  const commonProps = {
    id: params.item.id,
    gridId: params.gridId,
    gridSelectionGroupId: params.gridSelectionGroupId,
    isSmallItem: params.isSmallItem,
    className: params.className,
    style: params.style,
    showHoverOverlay: params.showHoverOverlay,
    showSelectionOverlay: params.showSelectionOverlay,
    showSelectionNumber: params.showSelectionNumber,
    onClicked: (e: React.MouseEvent, isDoubleClick: boolean): void => {
      params.onClicked(e, isDoubleClick);
    },
    isSkeletion: false,
    hasTransparency: params.hasTransparency,
    tag: (params.item.tag as GridMediaItemTag) ?? undefined,
    containerClassName: (params.item.containerClassName as string) ?? undefined,
  };

  const getThumbSrcFromStockType = (stockType: PMWStockItemType): string => {
    switch (stockType) {
      case PMWStockItemType.ANIMATED_STICKER:
        return getPMWStockAnimatedStickerThumbURL(params.item.hashedFilename);
      case PMWStockItemType.SHAPE:
        return getPMWStockShapeThumbURL((params.item.idPMWStock as GridPMWStockID).idPMWStockShape!);
      case PMWStockItemType.ICON:
        return getPMWStockIconThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.AI_ICON:
        return getPMWStockAiIconThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.STILL_STICKER:
        return getPMWStockStillStickerThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.AI_STICKER:
        return getPMWStockAiStickerThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.IMAGE:
        return getPMWStockImageThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.EMOJI:
        return getPMWStockEmojiThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.EXTRACTED_GETTY_STICKER:
        return getPMWStockExtractedGettyStickerThumbURL(params.item.hashedFilename as string);
      case PMWStockItemType.AI_ANIMATED_STICKER:
        return getPMWStockAiAnimatedStickerThumbURL(params.item.hashedFilename as string);
      default:
        throw new Error(`PMWStockItemType not handled: ${stockType}`);
    }
  };

  switch (params.item.type) {
    case GRID_ITEM_TYPE.CLIPART:
      if (shouldHideClipartItem(params.item)) {
        return null;
      }
      return <GridClipartItem key={params.item.id} {...commonProps} paddedThumb={true} thumbSrc={params.item.thumbSrc} />;

    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
      return <GridShapeItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockShapeThumbURL(params.item.hashedFilename)} />;

    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
      return <GridAnimatedStickerItem key={params.item.id} {...commonProps} videoThumbSrc={getPMWStockAnimatedStickerThumbURL(params.item.hashedFilename)} />;
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return <GridAnimatedStickerItem key={params.item.id} {...commonProps} videoThumbSrc={getPMWStockAiAnimatedStickerThumbURL(params.item.hashedFilename)} />;

    case GRID_ITEM_TYPE.USER_IMAGE:
      return (
        <GridUserImageItem
          key={params.item.id}
          {...commonProps}
          thumbSrc={repoImageThumbURL(params.item.hashedFilename, params.item.extension)}
          uploaderName={params.item?.uploaderName}
          gridItemWidth={params.item?.gridItemWidth}
          isHidden={params.item?.isHidden}
        />
      );

    case GRID_ITEM_TYPE.USER_VIDEO:
      return (
        <GridUserVideoItem
          {...commonProps}
          key={params.item.id}
          containerClassName={params.containerClassName}
          imgThumbSrc={repoVideoThumbImageURL(params.item.hashedFilename, params.item.extension)}
          videoThumbSrc={repoVideoThumbURL(params.item.hashedFilename, params.item.extension)}
          uploaderName={params.item?.uploaderName}
          duration={params.item?.duration}
          gridItemWidth={params.item?.gridItemWidth}
          isHidden={params.item?.isHidden}
        />
      );
    case GRID_ITEM_TYPE.USER_AUDIO:
      return (
        <GridUserAudioItem
          key={params.item.id}
          {...commonProps}
          imgthumbSrc={repoAudioThumbImageURL(params.item.hashedFilename)}
          audioSrc={getAudioUrl(params.item.hashedFilename, params.item.source)}
          uploaderName={params.item?.uploaderName}
          duration={params.item?.duration}
          name={params.item?.name}
          hashedFilename={params.item?.hashedFilename}
          gridItemWidth={params.item?.gridItemWidth}
          isHidden={params.item?.isHidden}
        />
      );

    case GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER:
      return <GridExtractedGettyStickerItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockExtractedGettyStickerThumbURL(params.item.hashedFilename)} />;
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
      return <GridStillStickerItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockStillStickerThumbURL(params.item.hashedFilename)} />;
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return <GridStillStickerItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockAiStickerThumbURL(params.item.hashedFilename)} />;

    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
      return <GridIconItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockIconThumbURL(params.item.hashedFilename)} />;
    case GRID_ITEM_TYPE.PMW_AI_ICON:
      return <GridIconItem key={params.item.id} {...commonProps} thumbSrc={getPMWStockAiIconThumbURL(params.item.hashedFilename)} />;

    case GRID_ITEM_TYPE.IMAGE:
      return <GridImageItem key={params.item.id} {...commonProps} thumbSrc={params.item.thumbSrc} paddedThumb={params.paddedThumb} isLoading={params.item.isLoading} />;

    case GRID_ITEM_TYPE.VIDEO:
      return (
        <GridVideoItem
          key={params.item.id}
          {...commonProps}
          showDurationTag={params.item.showDurationTag}
          duration={params.item.duration}
          imgThumbSrc={params.item.thumbSrc}
          videoThumbSrc={params.item.videoSrc}
          isLoading={params.item.isLoading}
          showVideoTag={params.item.showVideoTag}
        />
      );

    case GRID_ITEM_TYPE.MULTIPAGE:
      return (
        <GridMultipageItem
          key={params.item.id}
          {...commonProps}
          previewURLs={params.item.previewURLs}
          fallbackPreviewURLs={params.item.fallbackPreviewURLs}
          videoPreviewURLs={params.item.videoPreviewURLs}
          pagesIsVideo={params.item.pagesIsVideo}
          pageCount={params.item.pageCount}
          isLoading={params.item.isLoading}
        />
      );

    case GRID_ITEM_TYPE.FANCYTEXT:
      // todo: replace thumbSrc prop with a call to a function getThumbSrc once all predefined fancytexts are finalised by Zohair
      //  (getThumbSrc will generate a URL by making a call to the controller at the endpoint Fancytextrender.output render)
      return (
        <GridFancytextItem key={params.item.id} {...commonProps} thumbSrc="https://d1csarkz8obe9u.cloudfront.net/texteffects/small/32.jpg?" paddedThumb={params.paddedThumb} />
      );

    case GRID_ITEM_TYPE.BUTTON:
      return (
        <GridButtonItem
          key={params.item.id}
          {...commonProps}
          iconSize={params.item.iconSize}
          icon={params.item.icon}
          text={params.item.text}
          className={params.item.className}
          flexDirection={params.item.flexDirection}
          applyCustomStyles={params.item.applyCustomStyles ?? true}
          containerClassName={params.item.containerClassName}
        />
      );

    case GRID_ITEM_TYPE.GENERIC_BUTTON:
      return <GridGenericButtonItem key={params.item.id} {...commonProps} onClicked={params.item.onClicked} buttonProps={params.item.buttonProps} />;

    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return (
        <GridStockVideoItem
          key={params.item.id}
          {...commonProps}
          containerClassName={params.containerClassName}
          videoThumbSrc={params.item.videoThumbSrc}
          imgThumbSrc={params.item.imgThumbSrc}
          isUHD={params.item.isUHD}
          duration={params.item.duration}
          isLoading={params.item.isLoading}
          bottomContent={params.item.bottomContent}
        />
      );

    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return (
        <GridStockImageItem
          {...commonProps}
          bottomContent={params.item.bottomContent}
          key={params.item.id}
          paddedThumb={params.paddedThumb}
          thumbSrc={params.item.thumbSrc}
          isLoading={params.item.isLoading}
        />
      );

    case GRID_ITEM_TYPE.COLLECTION:
      return (
        <GridCollectionItem
          key={params.item.id}
          {...commonProps}
          isShared={params.item.isShared}
          collectionName={params.item.collectionName}
          multiMediaType={params.item.multiMediaType}
        />
      );

    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return (
        <GridStockAudioItem
          {...commonProps}
          key={params.item.id}
          id={params.item.id}
          gridItemHeight={params.item.gridItemHeight}
          gridItemWidth={params.item.gridItemWidth}
          audioSrc={params.item.audioSrc}
          imgthumbSrc={params.item.imgThumbSrc}
          name={params.item.name}
          duration={params.item.duration}
          categories={[params.item.data.mood, params.item.data.genre]}
        />
      );

    case GRID_ITEM_TYPE.EMAIL_CAMPAIGN_AUDIENCE:
      return <GridEmailCampaignAudienceItem key={params.item.id} {...commonProps} id={parseInt(params.item.id)} />;

    case GRID_ITEM_TYPE.EMAIL_SIGNUP_FORM:
      return <GridEmailSignupFormItem key={params.item.id} {...commonProps} id={params.item.id} />;

    case GRID_ITEM_TYPE.SKELETON:
      return (
        <GridSkeletonItem
          key={params.item.id}
          styles={params.style}
          useFixedDimensions={params.item.useFixedDimensions}
          animationType={params.item.animationType}
          width={params.item.width}
          height={params.item.height}
        />
      );

    case GRID_ITEM_TYPE.CUSTOM:
      return <GridItem key={params.item.id} {...commonProps} {...params.item} />;

    case GRID_ITEM_TYPE.PMW_STOCK_MEDIA:
      return (
        <GridPMWStockMediaItem
          key={params.item.id}
          {...commonProps}
          id={params.item.id}
          thumbSrc={getThumbSrcFromStockType(params.item.stockType)}
          paddedThumb={params.paddedThumb}
          keywords={params.item.keywords}
          description={params.item.description}
          isEdited={params.item.isEdited}
          isDeleted={params.item.isDeleted}
          stockType={params.item.stockType}
          scrollToItem={params.item.scrollToItem}
          adminScore={params.item.adminScore}
        />
      );

    default:
      console.error(`Unhandled Grid item type: ${params.item.type}`);
      return null;
  }
}
